import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import PageHeader from "../../components/PageHeader";
import Loader from "../../components/Loader";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosget, axiospost } from "../../helpers/httprequest";
import { localRequest } from "../../helpers/uri";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import {
  FiUploadCloud,
  FiImage,
  FiCheckCircle,
  FiX,
  FiCamera,
  FiAlertCircle,
} from "react-icons/fi";

const Photo = ({ wizardMode = false }) => {
  const queryClient = useQueryClient();
  const { data: dashboardData } = useQuery({
    queryKey: ["dashboardDataset"],
    queryFn: async () => {
      const req = await axiosget("ms_dashboard");
      return req?.data;
    },
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["bioDataForPhoto"],
    queryFn: async () => {
      const req = await axiosget("ms_applicant/bio");
      return req?.data;
    },
  });

  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    const maxSize = 2 * 1024 * 1024;
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];

    if (!allowedTypes.includes(file.type)) {
      return "Only JPG, JPEG, and PNG files are allowed";
    }
    if (file.size > maxSize) {
      return "File size must be less than 2MB";
    }
    return null;
  };

  const handleFileSelect = (file) => {
    setUploadError(null);
    const error = validateFile(file);
    if (error) {
      setUploadError(error);
      return;
    }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('photo', selectedFile);
      setUploadProgress(30);

      const token = localStorage.getItem("isApplicant");
      const response = await fetch(`${localRequest}upload_passport`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

      const responseData = await response.json();
      setUploadProgress(60);

      if (response.ok && responseData?.type === 'success') {
        const photoUrl = responseData?.data;

        if (photoUrl) {
          setUploadProgress(80);

          const saveResponse = await axiospost('ms_applicant/photo', {
            link: photoUrl
          });

          if (saveResponse?.data?.type === 'success' || saveResponse?.status === 200) {
            setUploadProgress(100);

            setTimeout(async () => {
              setIsUploading(false);
              setSelectedFile(null);
              setPreviewUrl(null);
              await refetch();
              queryClient.invalidateQueries({ queryKey: ["dashboardDataset"] });
            }, 500);
          } else {
            throw new Error('Failed to save photo information');
          }
        } else {
          throw new Error('No photo URL in response');
        }
      } else {
        throw new Error(responseData?.message || 'Upload failed');
      }
    } catch (err) {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadError(err.message || "Upload failed. Please try again.");
      console.error('Upload error:', err);
    }
  };

  const hasPhoto = data?.photo?.link;

  const photoSrc = hasPhoto
    ? data.photo.link.startsWith('http')
      ? data.photo.link
      : `${localRequest}${data.photo.link.replace(/^\//, '')}`
    : null;

  return (
    <>
      {!wizardMode && <PageHeader
        title="Passport Photo"
        subtitle="Upload a clear passport-style photograph for your application"
        icon={<FiCamera className="w-5 h-5" />}
      />}

      {isLoading && <Loader />}
      {error && <Alert variant="danger"><AlertDescription>{`Local server error: ${error}`}</AlertDescription></Alert>}
      {data?.type !== undefined && <Alert variant="danger"><AlertDescription>{`${data?.message}`}</AlertDescription></Alert>}

      {!isLoading && !error && data?.type === undefined && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="max-w-2xl mx-auto"
        >
          {/* Photo Display */}
          <div className="flex flex-col items-center mb-6">
            <div className="relative group">
              <div className={`w-48 h-56 rounded-xl overflow-hidden border-2 ${
                previewUrl ? "border-primary-300" : hasPhoto ? "border-success-200" : "border-neutral-200 border-dashed"
              } bg-neutral-50 flex items-center justify-center`}>
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                ) : photoSrc ? (
                  <img
                    src={photoSrc}
                    alt="Passport Photograph"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}

                {!previewUrl && !photoSrc && (
                  <div className="flex flex-col items-center gap-2 text-neutral-400">
                    <FiImage className="h-12 w-12" />
                    <span className="text-xs font-medium">No photo</span>
                  </div>
                )}

                {!previewUrl && photoSrc && (
                  <div className="hidden items-center justify-center h-full">
                    <FiImage className="h-12 w-12 text-neutral-400" />
                  </div>
                )}
              </div>

              {previewUrl && (
                <button
                  onClick={handleClear}
                  className="absolute -top-2 -right-2 p-1.5 bg-white rounded-full shadow-md border border-neutral-200 hover:bg-neutral-50 transition-colors"
                >
                  <FiX className="h-3.5 w-3.5 text-neutral-600" />
                </button>
              )}
            </div>

            <div className="mt-3">
              {previewUrl ? (
                <Badge variant="info" size="sm">Ready to upload</Badge>
              ) : hasPhoto ? (
                <Badge variant="success" dot size="sm">Photo uploaded</Badge>
              ) : (
                <Badge variant="warning" size="sm">Photo required</Badge>
              )}
            </div>

            {selectedFile && (
              <p className="mt-2 text-xs text-neutral-500">
                {selectedFile.name} ({(selectedFile.size / 1024).toFixed(0)} KB)
              </p>
            )}
          </div>

          {/* Upload Progress */}
          {isUploading && (
            <div className="mb-4 space-y-2">
              <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${uploadProgress}%` }}
                  className="h-full bg-primary-600 rounded-full"
                />
              </div>
              <p className="text-xs text-center text-neutral-500">
                Uploading... {uploadProgress}%
              </p>
            </div>
          )}

          {/* Error Message */}
          {uploadError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2"
            >
              <FiAlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{uploadError}</p>
            </motion.div>
          )}

          {/* Actions */}
          {previewUrl && !isUploading ? (
            <div className="flex gap-3 mb-6">
              <Button
                variant="outline"
                fullWidth
                onClick={handleClear}
                leftIcon={<FiX className="h-4 w-4" />}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                fullWidth
                onClick={handleUpload}
                leftIcon={<FiCheckCircle className="h-4 w-4" />}
              >
                Upload Photo
              </Button>
            </div>
          ) : !isUploading ? (
            <div
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`mb-6 border-2 border-dashed rounded-lg p-5 transition-all duration-200 cursor-pointer ${
                isDragging
                  ? "border-primary-500 bg-primary-50"
                  : "border-neutral-300 hover:border-primary-400 bg-neutral-50/50"
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleFileInputChange}
                className="hidden"
              />
              <div className="flex flex-col items-center gap-2">
                <FiUploadCloud className={`h-8 w-8 ${isDragging ? "text-primary-600" : "text-neutral-400"}`} />
                <div className="text-center">
                  <p className="text-sm text-neutral-600">
                    <span className="text-primary-600 font-medium">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-neutral-400 mt-0.5">
                    JPG, JPEG, PNG (max 2MB)
                  </p>
                </div>
              </div>
            </div>
          ) : null}

          {/* Requirements */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center text-xs text-neutral-500">
            <span>Passport-style photo</span>
            <span className="text-neutral-300">|</span>
            <span>Clear face visible</span>
            <span className="text-neutral-300">|</span>
            <span>White or light background</span>
            <span className="text-neutral-300">|</span>
            <span>Max 2MB</span>
          </div>
        </motion.div>
      )}
    </>
  );
};

export default Photo;
